import { TranslateService } from "@ngx-translate/core";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { toggleAnimation } from "src/app/shared/animations";
import { PopupType } from "src/app/shared/enums/popup-types";
import { MessagePortalComponent } from "src/app/portal/portal";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { BillService } from "src/app/shared/services/bill.service";
import { Bill, BillStatus } from "src/app/shared/types/contract-types";
import { PositioningService } from "src/app/shared/services/positioning.service";
import { DatatableColumn } from "src/app/shared/components/base-table/base-table.component";
import { Component, ElementRef, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";

@Component({
  selector: "billing-component",
  templateUrl: "./billing-page.component.html",
  standalone: false,
  animations: [toggleAnimation],
})
export class BillingComponent implements OnInit {
  bills: Bill[] = [];
  filteredTasks: Bill[] = [];
  pagedTasks: Bill[] = [];
  pager: any = {};
  searchTask = "";
  selectedTab: BillStatus = BillStatus.Unpaid;
  isShowTaskMenu = false;
  isLoadingDelete = false;
  billToDelete?: number;
  @ViewChild("dropdownPlaceholder") dropdownPlaceholder!: ElementRef;
  @ViewChild("deleteConfirmationTemplate") deleteConfirmationTemplate!: TemplateRef<unknown>;
  @ViewChild(MessagePortalComponent) messagePortalComponent!: MessagePortalComponent;

  BillStatus = BillStatus;

  search = "";
  isLoading = false;
  cols: DatatableColumn[] = [];

  deleteModal!: DialogRef;
  private dialog = inject(DialogService);

  constructor(
    private billService: BillService,
    private translate: TranslateService,
    private positioningService: PositioningService,
  ) {}

  ngOnInit(): void {
    this.loadBills();
    this.updateColumnTranslations();

    this.translate.onLangChange.subscribe(() => {
      this.updateColumnTranslations();
    });
  }

  private updateColumnTranslations(): void {
    this.cols = [
      { field: "contractName", title: this.translate.instant("contract.customer_name") } as DatatableColumn,
      { field: "startDate", title: this.translate.instant("billing.start_date") } as DatatableColumn,
      { field: "endDate", title: this.translate.instant("billing.end_date") } as DatatableColumn,
      { field: "createdDate", title: this.translate.instant("billing.created_date") } as DatatableColumn,
      { field: "price", title: this.translate.instant("billing.price") } as DatatableColumn,
      { field: "deliveryStatus", title: this.translate.instant("billing.delivery_status"), hide: true } as DatatableColumn,
      { field: "status", title: this.translate.instant("billing.status") } as DatatableColumn,
      { field: "actions", title: this.translate.instant("bills.actions"), sort: false } as DatatableColumn,
    ];
  }

  loadBills(): void {
    this.isLoading = true;
    const bilsSubscription = this.billService
      .getAllBills()
      .subscribe((bills) => {
        this.isLoading = false;
        bilsSubscription.unsubscribe();
        this.bills = bills;
        this.tabChanged(BillStatus.Unpaid);
      });
  }

  searchTasks(text: string, resetPage: boolean = true): void {
    const filtered = this.bills.filter(bill =>
      bill.filepath.toLowerCase().includes(text.toLowerCase()) || bill.contractName?.toLocaleLowerCase().includes(text.toLocaleLowerCase()),
    );
    this.filteredTasks = filtered;
    if (resetPage) {
      this.setPage(1);
    }
  }

  setPage(page: number): void {
    const totalItems = this.filteredTasks.length;
    const itemsPerPage = 10;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (page < 1)
      page = 1;
    if (page > totalPages)
      page = totalPages;

    this.pager.currentPage = page;
    this.pager.totalPages = totalPages;
    this.pager.startIndex = (page - 1) * itemsPerPage;
    this.pager.endIndex = Math.min(page * itemsPerPage, totalItems) - 1;

    this.pagedTasks = this.filteredTasks.slice(
      this.pager.startIndex,
      this.pager.endIndex + 1,
    );
  }

  tabChanged(status: BillStatus): void {
    this.selectedTab = status;
    this.filteredTasks = this.bills.filter(bill => bill.status === status);
    this.setPage(1);
  }

  getDeliveryStatusClass(status: boolean): string {
    return status ? "badge-outline-success" : "badge-outline-danger";
  }

  updateStatus(id: number, status: number) {
    const updateSubscription = this.billService.updateBillStatus(id, status).subscribe(() => {
      updateSubscription.unsubscribe();
      showMessage(PopupType.Success, this.translate.instant("bills.success_status_update"));
      this.loadBills();
    });
  }

  actionsFormatter(cell: any, row: any): string {
    return `
      <button type="button" class="btn btn-primary" (click)="editTask(${
        row.id
      })">${this.translate.instant("billing.edit")}</button>
      <button type="button" class="btn btn-danger" (click)="deleteTask(${
        row.id
      })">${this.translate.instant("billing.delete")}</button>
    `;
  }

  onPortalAttached() {
    setTimeout(() => {
      this.positioningService.setPosition(this.dropdownPlaceholder, this.messagePortalComponent);
    }, 0);
  }

  openDeleteModal(billId: number) {
    this.billToDelete = billId;
    this.deleteModal = this.dialog.open(this.deleteConfirmationTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  closeDeleteModal() {
    this.deleteModal.close();
  }

  handleDelete() {
    if (this.billToDelete) {
      this.isLoadingDelete = true;
      const deleteSubscription = this.billService.deleteBillById(this.billToDelete).subscribe({
        next: () => {
          deleteSubscription.unsubscribe();
          this.isLoadingDelete = false;
          showMessage(PopupType.Success, this.translate.instant("billing.successfull_delete"));
          this.closeDeleteModal();
          this.loadBills();
        },
        error: (err) => {
          deleteSubscription.unsubscribe();
          this.isLoadingDelete = false;
          showMessage(PopupType.Danger, err.error);
        },
      });
    }
  };

  updateColumn(col: DatatableColumn) {
    col.hide = !col.hide;
    this.cols = [...this.cols];
  }
}
